import { ENDPOINTS } from "../utils/appConfig";
import { fetchWithAuthRetry, getAuthHeaders } from "../utils/helpers";

async function parse(response) {
  if (!response.ok) {
    let detail = `${response.status}: ${response.statusText}`;
    try {
      const body = await response.json();
      detail = body.detail || body.error || JSON.stringify(body);
    } catch (_) { /* keep default */ }
    throw new Error(detail);
  }
  return response.json();
}

export async function fetchSurveyPlans(projectId) {
  const query = projectId
    ? `?project_id=${encodeURIComponent(projectId)}`
    : "";
  const response = await fetchWithAuthRetry(
    `${ENDPOINTS.SURVEY_PLANS}${query}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  return parse(response);
}

export async function fetchSurveyPlan(planId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_DETAIL(planId), {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return parse(response);
}

export async function createSurveyPlan(payload) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLANS, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parse(response);
}

export async function updateSurveyPlan(planId, payload) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_DETAIL(planId), {
    method: "PATCH",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parse(response);
}

export async function deleteSurveyPlan(planId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_DETAIL(planId), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  return true;
}

/** Ask the server to (re)generate the authoritative waypoint list. */
export async function generateSurveyPlanWaypoints(planId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_GENERATE(planId), {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return parse(response);
}

/** Upload a plan to a drone — creates SurveyFlight and publishes MQTT mission. */
export async function uploadSurveyPlan(planId, droneId) {
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_UPLOAD(planId), {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ drone_id: droneId }),
  });
  return parse(response);
}

/** Create a SurveyPlan from an uploaded KMZ/KML file (multipart). */
export async function importSurveyPlanKmz(file, planName, projectId) {
  const form = new FormData();
  form.append("file", file);
  if (planName) form.append("name", planName);
  if (projectId) form.append("project_id", projectId);
  const { Authorization } = getAuthHeaders();
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_IMPORT, {
    method: "POST",
    headers: { Authorization },
    body: form,
  });
  return parse(response);
}

/** Download a saved plan as a KMZ archive (blob + suggested filename). */
export async function fetchSurveyPlanKmz(planId) {
  const { Authorization } = getAuthHeaders();
  const response = await fetchWithAuthRetry(ENDPOINTS.SURVEY_PLAN_EXPORT(planId), {
    method: "GET",
    headers: { Authorization },
  });
  if (!response.ok) {
    let detail = `${response.status}: ${response.statusText}`;
    try {
      const body = await response.json();
      detail = body.error || body.detail || detail;
    } catch (_) { /* keep default */ }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return { blob, filename: match ? match[1] : "survey-plan.kmz" };
}
